import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatMenuModule } from '@angular/material/menu'
import { MatRadioModule } from '@angular/material/radio'
import { MatTooltipModule } from '@angular/material/tooltip'
import { Store, select } from '@ngrx/store'
import { BUYER, CreditBalance, CreditPool, FileObject, ForexData, PaymentTermsDates, paymentTermsDatesForAccountType, SERVICE_PROVIDER, User } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { compact, keyBy } from 'lodash-es'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, from, of } from 'rxjs'
import { map, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { CreditPoolApiService } from 'src/api/credit-pool'
import { FileUploaderService } from 'src/api/file'
import { loadCurrencies, selectAllCurrencies, selectCurrencyEntities } from 'src/app/store/currencies'
import { loadPaymentMethods, selectAllPaymentMethods } from 'src/app/store/payment-methonds'
import { loadPaymentReferences, selectAllPaymentReferences } from 'src/app/store/payment-references'
import { loadUsers, selectTradersOptions, selectUserEntities } from 'src/app/store/users'
import { EpochFieldModule } from 'src/components/epoch/epoch-field/epoch-field.module'
import { ReactiveAsteriskModule } from 'src/components/reactive-asterisk/reactive-asterisk.module'
import { SelectSearchModule } from 'src/components/select-search/select-search.module'
import { FilesService } from 'src/pages/admin/trading/deals/deal-documents/files.service'
import { CreditPoolService } from 'src/services/data/credit-pool.service'
import { dayjs } from 'src/services/dayjs'
import { ModalModule } from 'src/shared/modal'
import { PipesModule } from 'src/shared/pipes/pipes.module'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { replayForm } from 'src/shared/utils/replay-form'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import { deferAfterRender } from 'src/shared/utils/defer-after-render'
import { environment } from 'src/environments/environment'
import { FxRatesService } from 'src/pages/admin/financial/fx-rates/fx-rates.service'
import { CompanyFormGroup, CreditFormGroup, enableDisableCreditForm } from '../company-form'
import { CreditApplicationsTableComponent } from './credit-applications-table.component'
import { AmountMaskOptions, amountMaskToNumber, parseAmountMaskOptions } from './company-credit-amount-mask'


@Component({
    selector: 'tc-company-credit-tab',
    templateUrl: './company-credit-tab.component.html',
    styleUrl: './company-credit-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideNgxMask()],
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatRadioModule,
        MatTooltipModule,
        SelectSearchModule,
        ReactiveAsteriskModule,
        ReactiveFormsModule,
        EpochFieldModule,
        PipesModule,
        FlexLayoutModule,
        ModalModule,
        NgxMaskDirective,
        CreditApplicationsTableComponent,
    ]
})
export class CompanyCreditTabComponent extends OnDestroyMixin implements OnInit {
  private readonly AuthApi = inject(AuthApiService)
  private readonly CreditPoolApi = inject(CreditPoolApiService)
  private readonly CreditPoolSvc = inject(CreditPoolService)
  private readonly Files = inject(FilesService)
  private readonly FileUploader = inject(FileUploaderService)
  private readonly store = inject(Store)
  private readonly toaster = inject(ToasterService)
  private readonly FxRates = inject(FxRatesService)

  constructor() {
    super()
    // Defer store dispatches until skeleton loader has been painted
    deferAfterRender(() => {
      this.store.dispatch(loadCurrencies({}))
      this.store.dispatch(loadPaymentMethods())
      this.store.dispatch(loadPaymentReferences({}))
      this.store.dispatch(loadUsers({}))
    })
  }

  protected paymentTermsDates: PaymentTermsDates;
  protected readonly fxRates$ = new BehaviorSubject<ForexData>(undefined)

  protected readonly CreditTypes = [
    { id: 'dcl', name: 'DCL Limit' },
    { id: 'insurance', name: 'Insurance Limit' },
  ]
  protected readonly SecureTypes = [
    { id: "cash", name: 'Cash Against Documents' },
    { id: "letter", name: 'Letter of Credit' },
    { id: "prepayment", name: 'Prepayment' },
  ]
  protected readonly OpenPaymentMethods = [
    { id: "wire_transfer", name: 'Wire Transfer Funds' },
  ]

  // input
  @Input({ required: true }) companyAccount: number
  @Input({ required: true }) companyForm: CompanyFormGroup
  @Input({ required: true }) creditForm: CreditFormGroup
  @Input({ required: true }) editable: boolean
  @Input({ required: true }) creditPool$: BehaviorSubject<DeepReadonly<CreditPool>>

  protected isLoading$ = new BehaviorSubject<boolean>(true)

  @ViewChild('insuranceStatementFile', { static: false }) insuranceStatementFile: ElementRef<HTMLInputElement>
  protected readonly insuranceStatementFile$ = new ReplaySubject<FileObject>(1)

  // state
  protected readonly balance$ = new ReplaySubject<CreditBalance>(1)
  protected creditPoolExists$: Observable<boolean>
  protected readonly files$ = new BehaviorSubject<Record<string, FileObject>>({})
  protected creditApplications$: Observable<{ file: FileObject; file_id?: string; type?: string }[]>

  protected isNotBuyer$: Observable<boolean>
  protected isBuyer$: Observable<boolean>
  protected isServiceProvider$: Observable<boolean>
  protected fulfilled$: Observable<DeepReadonly<Omit<CreditPool['onboarding']['fulfilledByCreditDepartment'], 'user'> & { user: User }>>
  protected currencyOptions$: Observable<AmountMaskOptions>
  protected readonly amountMaskToNumber = amountMaskToNumber
  protected fxRate$: Observable<number>
  protected creditLimitOfferCurrency$: Observable<number>
  protected inTransitLimitOfferCurrency$: Observable<number>
  protected dealCreationLimitOfferCurrency$: Observable<number>

  // feature flags
  protected readonly WA_17156_ENABLE_MULTI_CURRENCY_CREDIT_SECTION = environment.WA_17156_ENABLE_MULTI_CURRENCY_CREDIT_SECTION

  // form
  protected showPrepaymentPercent$: Observable<boolean>

  // ref data
  protected readonly currencies$ = this.store.pipe(select(selectAllCurrencies), waitNotEmpty())
  protected readonly paymentReferences$ = this.store.pipe(select(selectAllPaymentReferences), waitNotEmpty())
  protected readonly paymentMethods$ = this.store.pipe(select(selectAllPaymentMethods), waitNotEmpty())
  protected readonly traders$ = this.store.pipe(select(selectTradersOptions()), waitNotEmpty())


  ngOnInit() {
    combineLatest([
      this.currencies$,
      this.paymentReferences$,
      this.paymentMethods$,
      this.traders$
    ]).pipe(take(1)).subscribe(() => {
      this.isLoading$.next(false)
    })

    this.FxRates.getFxRates().then(r => this.fxRates$.next(r))

    this.companyForm.controls.type.valueChanges.pipe(
      untilComponentDestroyed(this),
      startWith(this.companyForm.controls.type.value),
    ).subscribe(type => {
      this.paymentTermsDates = paymentTermsDatesForAccountType(type)
    })

    this.isNotBuyer$ = replayForm(this.companyForm.controls.type).pipe(map(type => type !== BUYER))
    this.isBuyer$ = replayForm(this.companyForm.controls.type).pipe(map(type => type === BUYER))
    this.isServiceProvider$ = replayForm(this.companyForm.controls.type).pipe(map(type => type === SERVICE_PROVIDER))

    this.creditPoolExists$ = this.creditPool$.pipe(map(cp => !!cp.pool_id))
    this.fulfilled$ = combineLatest([
      this.creditPool$.pipe(map(creditPool => creditPool.onboarding?.fulfilledByCreditDepartment)),
      this.store.pipe(select(selectUserEntities), waitNotEmpty()),
    ]).pipe(map(([fulfilled, users]) => ({ ...fulfilled, user: users[fulfilled?.user]})))

    this.creditApplications$ = replayForm(this.creditForm).pipe(
      withLatestFrom(this.files$),
      switchMap(([creditForm, files]) => {
        const missingFileIds = compact(creditForm.credit_applications?.map(ca => !files[ca.file_id] && ca.file_id))
        if (!missingFileIds.length) return of([creditForm, files] as const)
        return from(this.Files.getFilesByIds(missingFileIds)).pipe(
          tap(newFiles => {
            files = { ...files, ...keyBy(newFiles, 'file_id') }
            this.files$.next(files)
          }),
          map(() => [creditForm, files] as const)
        )
      }),
      map(([creditForm, files]) =>
        creditForm.credit_applications?.map(ca => ({ ...ca, file: files[ca.file_id] }))))

    this.showPrepaymentPercent$ = replayForm(this.creditForm).pipe(map(creditForm =>
      creditForm.payment_term_type === 'secure' &&['prepayment', 'cash'].includes(creditForm.payment_term_secure_type)))

    const currencyEntities$ = this.store.pipe(select(selectCurrencyEntities), waitNotEmpty())
    this.currencyOptions$ = combineLatest([
      replayForm(this.creditForm.controls.currency),
      currencyEntities$,
    ]).pipe(
      map(([code, currencies]) => parseAmountMaskOptions(currencies[code])),
      startWith(parseAmountMaskOptions(undefined)))

    // FX Rate from Insurance Currency to Offer Currency
    this.fxRate$ = combineLatest([
      replayForm(this.creditForm.controls.currency),
      replayForm(this.companyForm.controls.pricing.controls.currency),
      this.fxRates$,
    ]).pipe(map(([fromCurrency, toCurrency, fxRates]) => this.getCurrencyConvertRate(fromCurrency, toCurrency, fxRates)))

    // Credit Limit - Offer Currency
    this.creditLimitOfferCurrency$ = combineLatest([
      replayForm(this.creditForm.controls.max),
      this.fxRate$,
    ]).pipe(map(([amount, rate]) => amount ? amount * rate : 0))

    // In Transit Limit - Offer Currency  
    this.inTransitLimitOfferCurrency$ = combineLatest([
      replayForm(this.creditForm.controls.in_transit_max),
      this.fxRate$,
    ]).pipe(map(([amount, rate]) => amount ? amount * rate : 0))

    // Deal Creation Limit - Offer Currency
    this.dealCreationLimitOfferCurrency$ = combineLatest([
      replayForm(this.creditForm.controls.deal_creation_max),
      this.fxRate$,
    ]).pipe(map(([amount, rate]) => amount ? amount * rate : 0))

    combineLatest([
      replayForm(this.creditForm),
      replayForm(this.companyForm),
    ]).pipe(untilComponentDestroyed(this)).subscribe(() => {
      enableDisableCreditForm(this.creditForm, this.companyForm, !this.editable)
    })

    // Load balance reactively when credit pool changes
    this.creditPool$.pipe(
      untilComponentDestroyed(this)
    ).subscribe(creditPool => {
      if (creditPool.pool_id) {
        this.loadBalance()
      }
    })
  }

  private async loadBalance() {
    const insuranceStatementFileId = this.creditPool$.value.attributes?.insurance_statement_file_id

    const [balance, insuranceStatementFile] = await Promise.all([
      this.creditPool$.value.pool_id ? this.CreditPoolSvc.getBalance(this.companyAccount).toPromise() : undefined,
      insuranceStatementFileId && this.Files.getFileById(insuranceStatementFileId),
    ])

    this.balance$.next(balance)
    if (insuranceStatementFileId) {
      this.insuranceStatementFile$.next(insuranceStatementFile)
    }
  }

  protected removeInsuranceStatementFile() {
    if (this.insuranceStatementFile?.nativeElement) this.insuranceStatementFile.nativeElement.files = undefined
    this.insuranceStatementFile$.next(undefined)
    const ctrl = this.creditForm.controls.insurance_statement_file_id
    ctrl.setValue(undefined)
    ctrl.markAsTouched()
    ctrl.markAsDirty()
  }

  protected uploadClicked() {
    if (!this.insuranceStatementFile.nativeElement.files.length) {
      this.insuranceStatementFile.nativeElement.click()
      return
    }
    this.creditPool$.pipe(take(1)).subscribe(cp => {
      if (!cp.pool_id) {
        this.toaster.warning('Credit pool is not ready. Please select currency and click "save" button.')
        return
      }

      this.FileUploader.uploadFile(this.insuranceStatementFile.nativeElement.files[0], { visibility: 0 }).subscribe(file => {
        this.insuranceStatementFile$.next(file)
        const ctrl = this.creditForm.controls.insurance_statement_file_id
        ctrl.setValue(file.file_id)
        ctrl.markAsTouched()
        ctrl.markAsDirty()
      })
    })
  }

  protected async reviewCredit() {
    const creditPool = await this.creditPool$.pipe(take(1)).toPromise()
    const { user_id } = this.AuthApi.currentUser

    try {
      const { data: cp } = await this.CreditPoolApi.update(this.companyAccount, {
        user_id,
        onboarding: {
          ...creditPool.onboarding,
          fulfilledByCreditDepartment: {
            isFulfilled: true,
            user: user_id,
            updated: dayjs.utc().unix(),
          },
        },
      })
      this.creditPool$.next(cp)
      this.toaster.success('Credit application review successfully.')
    } catch (err) {
      console.error('Unable to review credit application.', err)
      this.toaster.error('Unable to review credit application.', err)
    }
  }

  protected handleCreditApplicationChange(applications: { file_id: string; type: string }[]) {
    this.creditForm.markAllAsTouched()
    if (!this.creditForm.valid) {
      this.toaster.warning('Fill in all required fields first')
      return
    }

    const ctrl = this.creditForm.controls.credit_applications
    ctrl.setValue(applications)
    ctrl.markAsTouched()
    ctrl.markAsDirty()
  }

  protected validateCreditForm(): boolean {
    this.creditForm.markAllAsTouched()
    if (!this.creditForm.valid) {
      this.toaster.warning('Fill in all required fields first')
      return false
    }
    return true
  }

  // function addPaymentTerms() {
  //   $ctrl.creditPool.attributes.custom_payment_terms = $ctrl.creditPool.attributes.custom_payment_terms || []
  //   $ctrl.creditPool.attributes.custom_payment_terms.push({})
  // }
  protected getCurrencyConvertRate(from: string, to: string, fxRates: ForexData) {
    if (from === to) return 1
    if (!from || !to) return 0
    if (!fxRates) return 0
    const fromCurrency = from === 'CAD' ? { ask: 1, bid: 1 } : fxRates.rates[from].spot
    const toCurrency = to === 'CAD' ? { ask: 1, bid: 1 } : fxRates.rates[to].spot
    return fromCurrency.ask / toCurrency.ask
  }
}
